import VideoRenderer from 'components/VideoRenderer';
import { TabPanelInner } from 'components/Tabs';
import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  SelectedVirtualBackground,
  VirtualBackgroundItem,
} from 'features/virtual-backgrounds/types';
import BackgroundGrid from 'features/virtual-backgrounds/components/BackgroundGrid';
import { useVirtualBackgroundUpload } from 'features/virtual-backgrounds/hooks/useVirtualBackgroundUpload';
import {
  customBackgroundAdded,
  customBackgroundDeleted,
} from 'features/virtual-backgrounds/virtualBackgroundsSlice';
import { customVirtualBackgroundUploadRequested } from 'features/virtual-backgrounds/actions';
import {
  selectVirtualBackgroundFailure,
  selectVirtualBackgroundPersistenceEnabled,
} from 'features/virtual-backgrounds/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { v4 as uuidv4 } from 'uuid';
import { UppyFile } from '@uppy/core';
import { logger } from 'utils/logger';
import { logVBIntent } from 'utils/webrtc/virtual-background/gateTelemetry';

export type VideoBackgroundTabProps = {
  videoStream: MediaStream | null;
  onChange: (config: SelectedVirtualBackground) => void;
  onReset: () => void;
  selected: string;
  loading?: boolean;
  error?: boolean;
};

const CenteredBox = styled('div')({
  minHeight: '250px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const VideoBackgroundTab = ({
  videoStream,
  onChange,
  onReset,
  selected,
  loading,
  error,
}: VideoBackgroundTabProps) => {
  const { t } = useTranslation(['settings', 'common']);

  const dispatch = useAppDispatch();
  const failure = useAppSelector(selectVirtualBackgroundFailure);
  const persistenceEnabled = useAppSelector(selectVirtualBackgroundPersistenceEnabled);

  useEffect(() => {
    logVBIntent({ source: 'panel' });
  }, []);

  const handleUpload = (file: UppyFile<any, any>) => {
    const blobUrl = URL.createObjectURL(file.data as File);

    const background: VirtualBackgroundItem = {
      id: `custom-${uuidv4()}`,
      type: 'image',
      value: blobUrl,
      thumbnail: blobUrl,
      label: file.name,
      isCustom: true,
    };

    dispatch(customBackgroundAdded(background));

    if (persistenceEnabled) {
      dispatch(
        customVirtualBackgroundUploadRequested({ id: background.id, file: file.data as File })
      );
    }

    onChange(background);
  };

  const {
    handleFileSelect,
    isUploading,
    error: uploadError,
  } = useVirtualBackgroundUpload({
    onUpload: handleUpload,
  });

  const handleDelete = (background: VirtualBackgroundItem) => {
    if (background.isCustom) {
      if (background.value.startsWith('blob:')) {
        URL.revokeObjectURL(background.value);
      }
      if (background.thumbnail?.startsWith('blob:')) {
        URL.revokeObjectURL(background.thumbnail);
      }

      dispatch(customBackgroundDeleted({ id: background.id, persisted: background.persisted }));

      logger
        .remote({ action: true, capture: 'virtual-background' })
        .log(`The user deleted virtual background ${background.id}`);

      if (selected === background.value) {
        onReset();
      }
    }
  };

  if (error) {
    return (
      <TabPanelInner>
        <CenteredBox>
          <Typography>
            {t(
              failure === 'unsupported'
                ? 'settings:virtual_bg.unsupported'
                : 'common:generic_error_short'
            )}
          </Typography>
        </CenteredBox>
      </TabPanelInner>
    );
  }

  if (loading) {
    return (
      <TabPanelInner>
        <CenteredBox>
          <CircularProgress disableShrink />
        </CenteredBox>
      </TabPanelInner>
    );
  }

  return (
    <TabPanelInner>
      <VideoRenderer mediaStream={videoStream} sx={{ mb: 4 }} />
      {(failure === 'failed' || uploadError) && (
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography sx={{ color: 'error.main' }} variant="body2">
            {failure === 'failed' ? t('settings:virtual_bg.activation_error') : uploadError}
          </Typography>
        </Box>
      )}
      <Box sx={{ pt: 4 }}>
        <BackgroundGrid
          selected={selected}
          onChange={onChange}
          onReset={onReset}
          onUpload={handleFileSelect}
          isUploading={isUploading}
          onDelete={handleDelete}
        />
      </Box>
    </TabPanelInner>
  );
};

export default VideoBackgroundTab;
